"use client";

import React from "react";
import { Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function EditorsPicksConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="121776" className="grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-7xl mx-auto">
        <div className="lg:col-span-7 min-h-[400px] lg:min-h-[600px] rounded-xl bg-muted/10 animate-pulse border border-border/20" />
        <div className="lg:col-span-5 flex flex-col gap-6">
          {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex-1 min-h-[180px] rounded-xl bg-muted/10 animate-pulse border border-border/20" />)}
        </div>
      </div>;
  }
  if (error || !posts || posts.length === 0) {
    return <div className="text-center py-12 border border-border/30 rounded-xl bg-card/20 backdrop-blur-md max-w-7xl mx-auto">
        <p className="text-muted-foreground font-sans">Our latest financial strategies are being updated. Please check back shortly.</p>
      </div>;
  }
  const featuredPost = posts[0];
  const sidePosts = posts.slice(1, 4);
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-7xl mx-auto">
      {}
      {featuredPost && <Link to={featuredPost.link || "#"} className="group relative overflow-hidden rounded-xl border border-border/30 bg-card/40 lg:col-span-7 min-h-[400px] lg:min-h-[600px] flex flex-col justify-end p-6 lg:p-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10">
          {}
          <div className="absolute inset-0 z-0">
            <img src={featuredPost.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593423/img/creative_finance_hero.png"} alt={featuredPost.title} className="w-full h-full object-cover transition-all duration-1000 ease-out saturate-0 opacity-40 group-hover:saturate-100 group-hover:opacity-60 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/90 to-transparent opacity-95 group-hover:opacity-85 transition-opacity duration-700" />
          </div>

          {}
          <div className="relative z-10 flex flex-col gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="default" className="bg-primary text-primary-foreground border-none font-mono uppercase tracking-widest text-[10px] px-3 py-1">
                {featuredPost.categories?.[0]?.name || "Myth vs Fact"}
              </Badge>
              <div className="flex items-center gap-1.5 text-xs font-mono font-medium text-accent-foreground bg-accent/10 backdrop-blur-xl px-2.5 py-1 rounded-md border border-accent/20">
                <Clock className="w-3.5 h-3.5" />
                <span>6 min read</span>
              </div>
            </div>
            <h3 className="text-2xl md:text-5xl font-serif font-bold leading-tight text-foreground group-hover:text-primary transition-colors duration-300 line-clamp-3">
              {featuredPost.title}
            </h3>
            {featuredPost.excerpt && <p className="text-muted-foreground font-sans line-clamp-2 text-sm md:text-lg max-w-2xl leading-relaxed" dangerouslySetInnerHTML={{
          __html: featuredPost.excerpt
        }} />}
            <div className="mt-2">
              <span className="text-primary font-mono text-sm border-b border-primary/50 group-hover:border-primary transition-all pb-0.5">Read Strategy →</span>
            </div>
          </div>
        </Link>}

      {}
      {sidePosts.length > 0 && <div className="lg:col-span-5 flex flex-col gap-6">
          {sidePosts.map((post, i) => <Link key={post.id} data-index={i} to={post.link || "#"} className="group relative overflow-hidden rounded-xl border border-border/20 bg-card/30 flex-1 flex flex-col justify-end p-5 min-h-[200px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-all duration-500 hover:shadow-xl hover:shadow-primary/5">
              {}
              <div className="absolute inset-0 z-0">
                <img src={post.featuredImage || `/img/wealth_growth_${i + 1}.png`} alt={post.title} className="w-full h-full object-cover transition-all duration-1000 ease-out saturate-0 opacity-30 group-hover:saturate-100 group-hover:opacity-50 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/95 to-background/40 opacity-95 group-hover:opacity-85 transition-opacity duration-700" />
              </div>

              {}
              <div className="relative z-10 flex flex-col gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge variant="secondary" className="bg-secondary/50 backdrop-blur-md border border-white/5 text-foreground font-mono text-[9px] uppercase tracking-wider">
                    {post.categories?.[0]?.name || "Creative Strategy"}
                  </Badge>
                  <div className="flex items-center gap-1.5 text-[10px] font-mono font-medium text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    <span>4 min read</span>
                  </div>
                </div>
                <h4 className="text-lg md:text-xl font-serif font-bold leading-snug text-foreground group-hover:text-primary transition-colors duration-300 line-clamp-2">
                  {post.title}
                </h4>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                  <span className="text-primary font-mono text-xs border-b border-primary/30">View Analysis</span>
                </div>
              </div>
            </Link>)}
        </div>}
    </div>;
}
export default function EditorsFinancialPicks() {
  const wp_query_params = {
    per_page: 4,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="financial-literacy-insights" className="dark bg-background text-foreground py-16 md:py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 -translate-x-1/2 w-[600px] h-[600px] bg-primary/10 blur-[160px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 translate-x-1/2 w-[400px] h-[400px] bg-accent/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto mb-12 relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-4">
          <Badge variant="outline" className="border-primary/30 text-primary font-mono uppercase tracking-widest text-[10px] px-3">
            The Alchemist's Ledger
          </Badge>
          <h2 className="text-4xl md:text-6xl font-serif font-bold tracking-tight">
            Creative <span className="text-primary bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">Wealth</span> Insights
          </h2>
          <p className="text-muted-foreground font-sans mt-3 text-lg md:text-xl max-w-2xl leading-relaxed">
            Mastering the three-dimensional outlook on finance through non-traditional wealth strategies and living benefits.
          </p>
        </div>
        <Link to="/CreativeWealthPage" className="group text-sm font-mono font-bold text-primary hover:text-primary/80 transition-all flex items-center gap-2 uppercase tracking-widest">
          Explore the Archive <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">&rarr;</span>
        </Link>
      </div>

      <div className="relative z-10">
        <PostsProvider wp_query={{
        per_page: 4,
        orderby: "date",
        order: "desc"
      }}>
          <EditorsPicksConsumer />
        </PostsProvider>
      </div>
    </section>;
}